import React, { useEffect, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, HelpCircle, XCircle } from "lucide-react";
import "../styles/modal.css";

/**
 * Wattline ConfirmModal — design doc step 8.
 *
 * Radix Dialog fornisce: focus-trap, chiusura con Esc, role="dialog"
 * + aria-modal, focus return al trigger. Qui aggiungiamo:
 * - styling wl-* (max-w 480, radius 10, shadow elevated, overlay ink 40%)
 * - variante destructive (bottone pieno rosso solo nella conferma finale)
 * - verbi esatti nel bottone primario ("Approva richiesta", non "OK")
 *
 * Props
 *  - open, onOpenChange: controllati dall'utente
 *  - title: string (Space Grotesk 18)
 *  - description?: string
 *  - confirmLabel: string (verbo esatto)
 *  - cancelLabel?: string (default "Annulla")
 *  - variant?: "primary" | "danger"
 *  - loading?: bool → disabilita bottone conferma
 *  - onConfirm: async fn
 *  - testId?: string
 */
export default function ConfirmModal({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Annulla",
  variant = "primary",
  loading = false,
  onConfirm,
  testId,
}) {
  const cancelRef = useRef(null);

  // Sposta il focus iniziale su Annulla (safe default: azione non distruttiva)
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => cancelRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const Icon = variant === "danger" ? AlertTriangle : HelpCircle;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="wl-modal-overlay" data-testid={testId ? `${testId}-overlay` : undefined} />
        <Dialog.Content
          className="wl-modal"
          data-testid={testId}
          aria-modal="true"
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          <div className="wl-modal__header">
            <div className={`wl-modal__icon wl-modal__icon--${variant === "danger" ? "danger" : "warning"}`} aria-hidden="true">
              <Icon size={18} />
            </div>
            <div className="wl-modal__title-block">
              <Dialog.Title className="wl-modal__title">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="wl-modal__desc">
                  {description}
                </Dialog.Description>
              )}
            </div>
          </div>

          <div className="wl-modal__actions">
            <Dialog.Close asChild>
              <button
                ref={cancelRef}
                type="button"
                className="wl-btn wl-btn--ghost"
                disabled={loading}
                data-testid={testId ? `${testId}-cancel` : undefined}
              >
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              className={`wl-btn ${variant === "danger" ? "wl-btn--danger" : "wl-btn--primary"}`}
              disabled={loading}
              onClick={onConfirm}
              data-testid={testId ? `${testId}-confirm` : undefined}
            >
              {variant === "danger" ? <XCircle size={14} /> : null}
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
